import { usePermissions } from "../contexts/permission-context";
import { useTenant } from "../contexts/tenant-context";
import { normalizeAction } from "../utils/actionAliases";
import { CrudAction } from "../types/permissions";
 
export interface PermissionCheckResult {
  hasPermission: boolean;
  isLoading: boolean;
}

const GLOBAL_ADMIN_FEATURES = new Set([
  "accessmanagement",
  "onboarding",
  "access-tenant",
  "access-activity",
]);

const buildPermissionResult = (
  permissions: Record<string, Record<CrudAction, boolean>> | null,
  loading: boolean,
  isActingAsGlobalAdmin: boolean,
  features: string[],
  action: string
): PermissionCheckResult => {
  if (loading || !permissions) {
    return { hasPermission: false, isLoading: true };
  }

  const normalizedAction = normalizeAction(action) as CrudAction;

  if (isActingAsGlobalAdmin) {
    const hasGlobalAdminFeature = features.some((feature) =>
      GLOBAL_ADMIN_FEATURES.has(feature)
    );

    return { hasPermission: hasGlobalAdminFeature, isLoading: false };
  }

  const hasPermission = features.some((feature) => {
    const featurePermissions = permissions[feature];

    if (!featurePermissions) {
      return false;
    }

    return featurePermissions[normalizedAction] ?? false;
  });

  return { hasPermission, isLoading: false };
};
 
export const useHasPermission = (feature: string, action: string): boolean => {
  const result = useHasPermissionWithLoading(feature, action);
  return result.hasPermission;
};

export const useAnyPermission = (features: string[], action: string): boolean => {
  const result = useAnyPermissionWithLoading(features, action);
  return result.hasPermission;
};
 
export const useHasPermissionWithLoading = (feature: string, action: string): PermissionCheckResult => {
  return useAnyPermissionWithLoading([feature], action);
};

export const useAnyPermissionWithLoading = (features: string[], action: string): PermissionCheckResult => {
  const { permissions, loading } = usePermissions();
  const { isGlobalAdmin, selectedTenantId } = useTenant();

  const isActingAsGlobalAdmin = isGlobalAdmin && selectedTenantId === '__GLOBAL_ADMIN__';

  return buildPermissionResult(
    permissions as Record<string, Record<CrudAction, boolean>> | null,
    loading,
    isActingAsGlobalAdmin,
    features,
    action
  );
};